import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

@Component({
  selector: 'app-inline-calendar',
  imports: [CommonModule],
  templateUrl: './inline-calendar.component.html',
  styleUrl: './inline-calendar.component.scss'
})
export class InlineCalendarComponent implements OnInit, OnChanges {
  @Input() selected: any = new Date();
  @Input() bookedDates: string[] = [];
  @Input() disablePast: boolean = true;
  @Input() disableFuture: boolean = false;
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Output() onDatePicked = new EventEmitter();
  @Output() onMonthChanged = new EventEmitter<Date>();

  /** currently visible month */
  visible = new Date();

  /** weekday labels (start Monday - change as needed) */
  weekdays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  monthGrid: (Date | null)[] = [];
  private bookedDateSet = new Set<string>();
  /** grid with null placeholders for leading blanks */
  // This method is used to handle the on init lifecycle hook
  ngOnInit(): void {
    this.syncBookedDateSet();
    this.syncVisibleMonthFromSelected();
    this.generateMonthGrid();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bookedDates']) {
      this.syncBookedDateSet();
    }
    if (changes['selected'] && changes['selected'].currentValue) {
      this.syncVisibleMonthFromSelected();
      this.generateMonthGrid();
    }
  }

  // This method is used to generate month grid
  generateMonthGrid() {
    const y = this.visible.getFullYear();
    const m = this.visible.getMonth();

    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const blanks = (first.getDay() + 6) % 7;

    const cells: (Date | null)[] = Array(blanks).fill(null);
    for (let d = 1; d <= last.getDate(); d++) {
      cells.push(new Date(y, m, d));
    }
    while (cells.length < 42) cells.push(null);
    this.monthGrid = cells;
  }

  /* helpers */


  /** returns true if this cell is before today */
  // This method is used to check if past using d
  isPast(d: Date) {
    let today: Date = new Date();
    today.setHours(0, 0, 0, 0);
    if (this.disablePast && d.getTime() < today.getTime()) {
      return true;
    }
    if (this.minDate) {
      const min = new Date(this.minDate);
      min.setHours(0, 0, 0, 0);
      if (d.getTime() < min.getTime()) {
        return true;
      }
    }
    if (this.maxDate) {
      const max = new Date(this.maxDate);
      max.setHours(0, 0, 0, 0);
      if (d.getTime() > max.getTime()) {
        return true;
      }
    }
    if (this.disableFuture && d.getTime() > today.getTime()) {
      return true;
    }
    return false;
  }

  /** highlight only when not-past and equals selected */
  // This method is used to check if selected using d
  isSelected(d: Date) {
    return !this.isPast(d) &&
      !!this.selected &&
      this.selected.toDateString() === d.toDateString();
  }

  isBooked(d: Date): boolean {
    return this.bookedDateSet.has(this.toDateKey(d));
  }

  isDisabled(d: Date): boolean {
    return this.isPast(d) || this.isBooked(d);
  }

  // This method is used to pick date using d
  pickDate(d: Date) {
    if (this.isDisabled(d)) {
      return;
    }
    this.selected = d;
    this.onDatePicked.emit(d);
  }

  // This method is used to return to the previous month
  prevMonth() {
    this.visible = new Date(
      this.visible.getFullYear(),
      this.visible.getMonth() - 1,
      1
    );
    this.generateMonthGrid();
    this.onMonthChanged.emit(new Date(this.visible));
  }
  // This method is used to move to the next month
  nextMonth() {
    this.visible = new Date(
      this.visible.getFullYear(),
      this.visible.getMonth() + 1,
      1
    );
    this.generateMonthGrid();
    this.onMonthChanged.emit(new Date(this.visible));
  }

  private syncBookedDateSet(): void {
    this.bookedDateSet = new Set(
      (this.bookedDates || [])
        .map((dateKey: any) => String(dateKey ?? '').trim())
        .filter((dateKey: string) => !!dateKey)
    );
  }

  private toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private syncVisibleMonthFromSelected(): void {
    if (!this.selected) {
      return;
    }
    const selectedDate = new Date(this.selected);
    if (isNaN(selectedDate.getTime())) {
      return;
    }
    this.visible = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
  }

}
